import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_publishable_key');

const PaymentForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [billingInfo, setBillingInfo] = useState({
    fullName: user?.displayName || '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'AU',
    phone: ''
  });
  const isTestPaymentEnabled = process.env.REACT_APP_ENABLE_TEST_PAYMENT === 'true';

  const handleBillingChange = (field, value) => {
    setBillingInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const requiredBillingFields = ['fullName', 'addressLine1', 'city', 'state', 'postalCode', 'country'];
    const missingField = requiredBillingFields.find((field) => !billingInfo[field]?.trim());
    if (missingField) {
      setError('Please complete all required billing address fields.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create payment intent
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: 500000, // $5000 in cents
          currency: 'aud',
          userEmail: user?.email
        }),
      });

      const paymentIntentData = await response.json();

      if (response.status === 409 && paymentIntentData.alreadyPaid) {
        setError('Payment already completed for this account.');
        return;
      }

      if (!response.ok) {
        throw new Error(paymentIntentData.error || 'Unable to create payment intent');
      }

      const { clientSecret, paymentIntentId } = paymentIntentData;
      
      if (!clientSecret) {
        throw new Error('No client secret received from server');
      }

      // Confirm payment
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: billingInfo.fullName,
            email: user?.email,
            phone: billingInfo.phone || undefined,
            address: {
              line1: billingInfo.addressLine1,
              line2: billingInfo.addressLine2 || undefined,
              city: billingInfo.city,
              state: billingInfo.state,
              postal_code: billingInfo.postalCode,
              country: billingInfo.country,
            },
          },
        },
      });

      if (result.error) {
        setError(result.error.message);
      } else {
        if (result.paymentIntent.status === 'succeeded') {
          setSuccess(true);
          // Confirm payment in backend
          try {
            await fetch(`${process.env.REACT_APP_API_URL}/api/payments/confirm-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                paymentIntentId: result.paymentIntent.id,
                userEmail: user?.email,
                billingInfo,
              }),
            });
            console.log('Payment confirmed successfully');
          } catch (confirmError) {
            console.error('Error confirming payment:', confirmError);
            // Don't show error to user since payment succeeded
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Payment failed. Please try again.');
      console.error('Payment error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#080808',
        color: '#ffffff',
        fontFamily: "'Instrument Sans', sans-serif"
      }}>
        <div style={{
          background: 'rgba(255,255,255,.02)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: '8px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{
            color: '#22c55e',
            fontSize: '48px',
            marginBottom: '20px'
          }}>
            ✓
          </div>
          <h2 style={{
            color: '#22c55e',
            fontSize: '24px',
            marginBottom: '15px'
          }}>
            Payment Successful!
          </h2>
          <p style={{
            color: '#9ca3af',
            marginBottom: '20px'
          }}>
            Thank you for your payment. You will receive a confirmation email shortly.
          </p>
          <button
            onClick={() => window.location.href = '/welcome'}
            style={{
              background: '#facc15',
              color: '#080808',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Continue to Welcome Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#080808',
      color: '#ffffff',
      fontFamily: "'Instrument Sans', sans-serif",
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <h1 style={{
          color: '#facc15',
          fontSize: '32px',
          fontWeight: '700',
          textAlign: 'center',
          marginBottom: '10px'
        }}>
          Complete Your Payment
        </h1>
        <p style={{
          color: '#9ca3af',
          textAlign: 'center',
          marginBottom: '40px'
        }}>
          Secure payment for KPL Ventures Program - AUD $5,000
        </p>

        <div style={{
          background: 'rgba(255,255,255,.02)',
          border: '1px solid rgba(250,204,21,.2)',
          borderRadius: '8px',
          padding: '40px'
        }}>
          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid #ef4444',
              color: '#ef4444',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#facc15',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                Billing Information
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <input
                  type="text"
                  placeholder="Full Name *"
                  value={billingInfo.fullName}
                  onChange={(e) => handleBillingChange('fullName', e.target.value)}
                  required
                  style={{
                    padding: '12px',
                    background: 'transparent',
                    border: '1px solid rgba(250,204,21,.3)',
                    borderRadius: '4px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
                <input
                  type="text"
                  placeholder="Phone (optional)"
                  value={billingInfo.phone}
                  onChange={(e) => handleBillingChange('phone', e.target.value)}
                  style={{
                    padding: '12px',
                    background: 'transparent',
                    border: '1px solid rgba(250,204,21,.3)',
                    borderRadius: '4px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>

              <input
                type="text"
                placeholder="Address Line 1 *"
                value={billingInfo.addressLine1}
                onChange={(e) => handleBillingChange('addressLine1', e.target.value)}
                required
                style={{
                  width: '100%',
                  marginTop: '12px',
                  padding: '12px',
                  background: 'transparent',
                  border: '1px solid rgba(250,204,21,.3)',
                  borderRadius: '4px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />

              <input
                type="text"
                placeholder="Address Line 2 (optional)"
                value={billingInfo.addressLine2}
                onChange={(e) => handleBillingChange('addressLine2', e.target.value)}
                style={{
                  width: '100%',
                  marginTop: '12px',
                  padding: '12px',
                  background: 'transparent',
                  border: '1px solid rgba(250,204,21,.3)',
                  borderRadius: '4px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <input
                  type="text"
                  placeholder="City *"
                  value={billingInfo.city}
                  onChange={(e) => handleBillingChange('city', e.target.value)}
                  required
                  style={{
                    padding: '12px',
                    background: 'transparent',
                    border: '1px solid rgba(250,204,21,.3)',
                    borderRadius: '4px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
                <input
                  type="text"
                  placeholder="State / Province *"
                  value={billingInfo.state}
                  onChange={(e) => handleBillingChange('state', e.target.value)}
                  required
                  style={{
                    padding: '12px',
                    background: 'transparent',
                    border: '1px solid rgba(250,204,21,.3)',
                    borderRadius: '4px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <input
                  type="text"
                  placeholder="Postal Code *"
                  value={billingInfo.postalCode}
                  onChange={(e) => handleBillingChange('postalCode', e.target.value)}
                  required
                  style={{
                    padding: '12px',
                    background: 'transparent',
                    border: '1px solid rgba(250,204,21,.3)',
                    borderRadius: '4px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
                <input
                  type="text"
                  placeholder="Country Code (e.g. AU) *"
                  value={billingInfo.country}
                  onChange={(e) => handleBillingChange('country', e.target.value.toUpperCase())}
                  required
                  maxLength={2}
                  style={{
                    padding: '12px',
                    background: 'transparent',
                    border: '1px solid rgba(250,204,21,.3)',
                    borderRadius: '4px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#9ca3af',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Card Information
              </label>
              <div style={{
                padding: '12px',
                background: 'transparent',
                border: '1px solid rgba(250,204,21,.3)',
                borderRadius: '4px',
                color: '#ffffff'
              }}>
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#ffffff',
                        '::placeholder': {
                          color: '#9ca3af',
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>

            <div style={{
              background: 'rgba(250,204,21,.05)',
              border: '1px solid rgba(250,204,21,.2)',
              borderRadius: '4px',
              padding: '20px',
              marginBottom: '30px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '10px'
              }}>
                <span style={{ color: '#9ca3af' }}>Program Fee</span>
                <span style={{ color: '#ffffff', fontWeight: '600' }}>AUD $5,000</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: '1px solid rgba(250,204,21,.2)',
                paddingTop: '10px'
              }}>
                <span style={{ color: '#ffffff', fontWeight: '600' }}>Total</span>
                <span style={{ color: '#facc15', fontWeight: '700', fontSize: '18px' }}>AUD $5,000</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={!stripe || loading}
              style={{
                width: '100%',
                padding: '16px',
                background: '#facc15',
                color: '#080808',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Processing Payment...' : 'Pay AUD $5,000'}
            </button>
          </form>

          {/* Test Payment Bypass Button - Development Only */}
          {isTestPaymentEnabled && (
          <button
            onClick={async () => {
              setLoading(true);
              setError('');
              try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/test-payment-success`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    userEmail: user?.email
                  }),
                });

                if (response.ok) {
                  // For test payments, also call confirm-payment to send emails
                  try {
                    await fetch(`${process.env.REACT_APP_API_URL}/api/payments/confirm-payment`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        paymentIntentId: 'test_' + Date.now(),
                        userEmail: user?.email,
                        billingInfo,
                      }),
                    });
                    console.log('Test payment confirmed');
                  } catch (confirmError) {
                    console.error('Error confirming test payment:', confirmError);
                  }
                  setSuccess(true);
                } else {
                  setError('Test payment failed');
                }
              } catch (err) {
                setError('Test payment failed');
                console.error('Test payment error:', err);
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              background: 'transparent',
              color: '#facc15',
              border: '1px solid #facc15',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              marginTop: '10px'
            }}
          >
            🔧 Test Payment Success (Dev Only)
          </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Payment = () => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm />
    </Elements>
  );
};

export default Payment;